import type { BaseAssetSettings } from '../../asset/BaseAsset';
import { getAttributesToSerialize } from '../../attributes';
import { CanvasTextAssetStyle } from '../../canvasText/asset/CanvasTextAssetStyle';
import { Transcript } from '../../text/Transcript';
import type { CanvasFancyCaptionRendererSettings } from '../renderer/CanvasFancyCaptionRenderer';
import { CanvasFancyCaptionRenderer } from '../renderer/CanvasFancyCaptionRenderer';
import type { CanvasTextStyleRendererSettings } from '../../canvasText/renderer/CanvasTextStyle';
import { CanvasTextStyleRenderer } from '../../canvasText/renderer/CanvasTextStyle';
import { BaseTextCanvasAsset } from '../../canvasRenderer/asset/BaseTextCanvasAsset';
import { tallestStringsFromTranscript } from '../../canvasText/metrics/CanvasTextMetrics';
import { CanvasFontSizeToFitAllTextBinarySearch } from '../../canvasText/metrics/CanvasFontSizeToFitAllTextBinarySearch';

export class CanvasFancyCaptionAsset extends BaseTextCanvasAsset<CanvasFancyCaptionRenderer> {
    override _classType = 'CanvasFancyCaptionAsset';

    style: CanvasTextAssetStyle;

    transcript: Transcript = new Transcript({
        parent: this,
        redrawParentOnUpdate: true,
        fetchResources: this.fetchResources,
        autoUpdate: this.autoDraw,
    });

    constructor(container, settings: BaseAssetSettings) {
        const renderer = new CanvasFancyCaptionRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            redrawParentOnUpdate: true,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        };
        this.style = new CanvasTextAssetStyle(styleSettings);
    }

    override get _styles() {
        // We only support one style for the moment, while some assets have normal + highlight style
        return { style: this.style };
    }

    get hasText() {
        return this.transcript.words.length !== 0;
    }

    override get stylePropertiesToCopy(): string[] {
        return [
            'textSize',
            'textAlign',
            'leadingMultiplier',
            'textScaleFactor',
            'maxHeight',
            'style',
        ];
    }

    /** returns the overlay's progress value to render a preview at the end of the first chunk **/
    getProgressForPreview() {
        const chunkIndex = 0;
        let { endTime } = this.renderer.getTranscriptChunkTimeRange(chunkIndex);

        // Since the chunk is visible from its start time until just before its end time, we subtract a small delta
        // time to get the time just before the chunk ends.
        if (endTime > 0) {
            endTime -= 1e-3;
        }

        const progress = (endTime - this.startTime) / (this.endTime - this.startTime);
        if (progress < 0 || progress > 1) {
            throw new Error('Unexpected progress value');
        }

        return progress;
    }

    /** Returns the number of chunks that the renderer has. */
    get chunkCount() {
        return this.renderer.chunkCount;
    }

    /** Sync the font size and leading to the style object. */
    _syncFontSizeAndLeadingToStyles() {
        const scaledFontSize = this.textSize * this.textScaleFactor;
        this.style.fontSize = scaledFontSize;
        this.style.leading = scaledFontSize * this.leadingMultiplier;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    override getCanvasRendererSettings(): CanvasFancyCaptionRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        return {
            ...super.getCanvasRendererSettings(),
            transcript: this.transcript,
            alignment: this.textAlign,
            wordWrap: this.wordWrap,
            style: this.style.getCanvasRendererSettings() as CanvasTextStyleRendererSettings,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            ...getAttributesToSerialize(this),
            'style',
        ]);
    }

    override set autoDraw(value: boolean) {
        super.autoDraw = value;
        // Because the transcript extends from TimeInvariantBase and isn't a BaseAsset,
        // we need to manually connect the two different update loops
        this.transcript.autoUpdate = value;
    }

    override get autoDraw() {
        return super.autoDraw;
    }

    override _setAutoDrawForAllChildren(value: boolean) {
        super._setAutoDrawForAllChildren(value);
        // connect the transcript to the autoDraw update loop
        this.transcript.autoUpdate = value;
    }

    /** Returns the maximum font size for the given font that can fit on one line. */
    getFontSizeToFitTallestText(fontSizeRange = { min: 50, max: 250 }) {
        const settings = this.getCanvasRendererSettings();
        const style = new CanvasTextStyleRenderer();
        style.apply(settings.style);

        const canvasFontSizeRange = {
            min: Math.floor(fontSizeRange.min * this.referenceToOutputMultiplier),
            max: Math.floor(fontSizeRange.max * this.referenceToOutputMultiplier),
        };

        // get the tallest text that can fit on one line
        const tallestText = this.renderer.hasChunks ?
            this.renderer.getTallestText() :
            tallestStringsFromTranscript(this.transcript, style.fontFamily).join('');

        // it's possible the transcript is empty (has no text)
        if (tallestText === '') {
            return {
                fontSize: this.style.fontSize,
                baseFontSize: Math.floor(this.style.fontSize / this.textScaleFactor),
                displaysAllText: true,
                tallestText: '',
            };
        }

        const { fontSize: canvasFontSize, displaysAllText } = CanvasFontSizeToFitAllTextBinarySearch.search({
            text: tallestText,
            alignment: settings.alignment,
            normalStyle: style,
            highlightStyle: style,
            highlightAreas: [],
            wordWrap: true,
            // since we're only after the tallest text that can fit on one line we don't need to worry about respecting
            // the current maxWidth which is why we're using a big number here. Also note that this is a canvas value,
            // not a luminary base value
            wordWrapWidth: 1920,
            maxHeight: settings.maxTextHeight,
            fontSizeRange: canvasFontSizeRange,
        });

        const assetFontSize = Math.floor(canvasFontSize / this.referenceToOutputMultiplier);

        return {
            fontSize: assetFontSize,
            baseFontSize: Math.floor(assetFontSize / this.textScaleFactor),
            displaysAllText,
            tallestText,
        };
    }
}

/** Syncs the given transcript to the given fancy caption asset. We do this by transferring the minimal amount of data
 * or words thereby reducing the number of chunks which has a positive impact on performance. */
export const syncTranscriptData = (asset: CanvasFancyCaptionAsset, transcript: Transcript) => {
    const offset = asset.slide.sceneGroup.trimOffset;
    const startTime = asset.slide.startTimeRelativeToSceneGroup + offset;
    const endTime = asset.slide.endTimeRelativeToSceneGroup + offset;

    asset.startTime = startTime;
    asset.endTime = endTime;
    asset.transcript.transcriptData = transcript.getDataForRange(startTime, endTime);
};
